import React from 'react';
import clsx from 'clsx';
import {i18n} from '@netx/core-next/locales';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSave as SaveIcon} from '@fortawesome/pro-solid-svg-icons';
import {LinearProgress} from '@mui/material';
import {Button, type ButtonProps} from '@netx/core-components/Button';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {
	submitButtonClasses,
	getSubmitButtonUtilityClass,
	SUBMIT_BUTTON_CLASS_NAME,
	type SubmitButtonClasses,
} from './submitButtonClasses';

type SubmitButtonUtiltiyClassProps = WithClasses<
	SubmitButtonClasses,
	{
		fullWidth?: boolean;
		iconOnly?: boolean;
		submitting?: boolean;
	}
>;

const useUtilityClasses = (ownerState: SubmitButtonUtiltiyClassProps) => {
	const {classes, fullWidth, iconOnly, submitting} = ownerState;
	const slots = {
		root: [
			'root',
			fullWidth && 'fullWidth',
			iconOnly && 'iconOnly',
			submitting && 'submitting',
		],
		progress: ['actions'],
		button: ['button'],
	};
	return composeClasses(slots, getSubmitButtonUtilityClass, classes);
};

const SubmitButtonRoot = styled('div', {
	name: SUBMIT_BUTTON_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}) => {
	const {fullWidth, iconOnly, submitting} =
		ownerState as SubmitButtonUtiltiyClassProps;
	return {
		display: 'inline-flex',
		position: 'relative',
		width: fullWidth ? '100%' : undefined,
		[`& .${submitButtonClasses.button}`]: {
			...(iconOnly
				? {
						height: 48,
						'& svg': {
							height: '20px !important',
							width: 20,
						},
				  }
				: null),
			...(submitting
				? {
						opacity: 0,
				  }
				: null),
		},
	};
});

const SubmitButtonButton = styled(Button, {
	name: SUBMIT_BUTTON_CLASS_NAME,
	slot: 'Button',
})(({theme}) => ({
	transition: theme.transitions.create('opacity'),
}));

const SubmitButtonProgress = styled(LinearProgress, {
	name: SUBMIT_BUTTON_CLASS_NAME,
	slot: 'Progress',
})(({theme}) => ({
	position: 'absolute',
	top: '50%',
	marginTop: -2,
	left: theme.spacing(1),
	right: theme.spacing(1),
}));

export interface SubmitButtonProps
	extends Omit<ButtonProps, 'classes'>,
		WithClasses<SubmitButtonClasses> {
	iconOnly?: boolean;
	label?: string;
	progress?: number | null;
	showWorking?: boolean;
}

export const SubmitButtonPrimitive = React.memo(
	({
		classes: _classes,
		className,
		fullWidth,
		iconOnly,
		label,
		progress,
		showWorking,
		...props
	}: SubmitButtonProps) => {
		const ownerState: SubmitButtonUtiltiyClassProps = {
			classes: _classes,
			fullWidth,
			iconOnly,
			submitting: showWorking,
		};

		const classes = useUtilityClasses(ownerState);

		return (
			<SubmitButtonRoot
				className={clsx(classes.root, className)}
				ownerState={ownerState}
			>
				<SubmitButtonButton
					color="primary"
					data-testid="submit-button"
					type="submit"
					variant="contained"
					{...props}
					fullWidth={fullWidth}
					className={classes.button}
				>
					{iconOnly ? (
						<FontAwesomeIcon icon={SaveIcon} />
					) : (
						label || i18n.t('i18n.submit')
					)}
				</SubmitButtonButton>
				{showWorking ? (
					<SubmitButtonProgress
						className={classes.progress}
						variant={progress ? 'determinate' : 'indeterminate'}
						value={progress || undefined}
					/>
				) : null}
			</SubmitButtonRoot>
		);
	},
);

export const SubmitButton = SubmitButtonPrimitive;

export default SubmitButton;
